import React, { useContext } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import MediaDuration from "./media/MediaDuration";
import ContentClassification from "./metadata/ContentClassification";
import InternalMediaComment from "./metadata/InternalMediaComment";
import TrailerSelect from "./metadata/TrailerSelect";
import ContentContext from "../../contexts/ContentContext";
import MasterMediaContext from "../../contexts/MasterMediaContext";
import { isAudioContent } from "../../utils/data/Content";
import DateTimePicker from "../common/tools/DateTimePicker";
import TabsMaster from "../content/TabsMaster";
import VariableList from "../variable_list/VariableList";

import type { JSX } from "react";
import type { SingleValue } from "react-select";

import type { GetMediaResponse, MediaChildren } from "../../api/MediaAPI.types";
import type { ContentContextValues } from "../content/ContentTabs";

export const PRODUCTION_YEAR_START = 1900;
export const PRODUCTION_YEAR_FUTURE_OFFSET = 3;

interface Option {
  value: string;
  label: string;
}

function TabContentMetadataWithoutMasterContext(): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { item, isDisabled, onItemUpdate } =
    useContext<ContentContextValues>(ContentContext);
  const { activeTab } = useContext(MasterMediaContext);

  const isAudio = isAudioContent(item);
  const isMaster = item.type === "master";
  const isFAST = item.type === "fast";

  const onProductionYear = (selection: SingleValue<Option>): void => {
    if (!selection) return;

    onItemUpdate({
      ...item,
      production_date: moment(selection.value, "YYYY").format("YYYY-MM-DD"),
    });
  };

  const getYears = (): Option[] => {
    const years: Option[] = [];
    const currentYear = moment().year() + PRODUCTION_YEAR_FUTURE_OFFSET;

    for (let x = currentYear; x >= PRODUCTION_YEAR_START; x -= 1) {
      years.push({ value: `${x}`, label: `${x}` });
    }
    return years;
  };

  const handleChange = (event: {
    target: { id: string; value: string | Date };
  }): void => {
    const { id, value } = event.target;
    onItemUpdate({ ...item, [id]: value } as GetMediaResponse);
  };

  const handleVariableListChange = (params: Record<string, string>): void => {
    onItemUpdate({ ...item, custom_params: params });
  };

  const onTrailer = (selection: SingleValue<Option>): void => {
    onItemUpdate({
      ...item,
      trailer_media_uuid: selection?.value ?? null,
    } as GetMediaResponse);
  };

  const formatedItem = isMaster
    ? item.children.find((child) => child.uuid === activeTab)
    : item;

  const mainMediaId = item.children.find(
    (child) => child.child_classification.key === "principal",
  )?.uuid;
  const isMasterFieldDisabled = isMaster && activeTab !== mainMediaId;
  const masterFieldTooltip = isMasterFieldDisabled
    ? t("masterFieldDisabledTooltip")
    : undefined;

  const handleDurationUpdate = (
    updatedDurationItem: GetMediaResponse | MediaChildren,
  ): void => {
    const { duration, duration_type: durationType } = updatedDurationItem;

    if (isMaster) {
      const updatedChildren = item.children.map((child) =>
        child.uuid === activeTab
          ? {
              ...child,
              duration,
              duration_type: durationType,
            }
          : child,
      );
      onItemUpdate({ ...item, children: updatedChildren });
    } else {
      onItemUpdate({
        ...item,
        duration,
        duration_type: durationType,
      });
    }
  };

  return (
    <div className="flex-1">
      <div className="section-separator">
        <div className="mb-2.5 flex flex-col">
          <div className="grid grid-cols-3 gap-5">
            {formatedItem ? (
              <MediaDuration
                item={formatedItem}
                onItemUpdate={handleDurationUpdate}
              />
            ) : null}
          </div>
        </div>
      </div>
      <div className="editor-form-section">
        <div className="editor-form-input-box-h">
          {!isAudio ? (
            <React.Fragment>
              <div
                className="editor-form-input"
                data-tooltip-content={masterFieldTooltip}
                data-tooltip-id={APP_TOOLTIP_ID}
              >
                <label
                  className="text-foreground py-1.25 text-[15px] font-medium"
                  htmlFor="isan_code"
                >
                  {t("isan_code")}
                </label>
                <input
                  className="disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
                  disabled={isDisabled || isMasterFieldDisabled}
                  id="isan_code"
                  maxLength={128}
                  onChange={handleChange}
                  placeholder="ISAN 0000-0000-D07A-0090-Q-0000-0000-X"
                  type="text"
                  value={item.isan_code ?? ""}
                />
              </div>
              <div
                className="editor-form-input"
                data-tooltip-content={masterFieldTooltip}
                data-tooltip-id={APP_TOOLTIP_ID}
              >
                <label
                  className="text-foreground py-1.25 text-[15px] font-medium"
                  htmlFor="eidr_code"
                >
                  {t("eidr_code")}
                </label>
                <input
                  className="disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
                  disabled={isDisabled || isMasterFieldDisabled}
                  id="eidr_code"
                  maxLength={128}
                  onChange={handleChange}
                  placeholder="10.5240/XXXX-XXXX-XXXX-XXXX-XXXX-C"
                  type="text"
                  value={item.eidr_code ?? ""}
                />
              </div>
            </React.Fragment>
          ) : (
            <div
              className="editor-form-input"
              data-tooltip-content={masterFieldTooltip}
              data-tooltip-id={APP_TOOLTIP_ID}
            >
              <label
                className="text-foreground py-1.25 text-[15px] font-medium"
                htmlFor="isrc_code"
              >
                {t("isrc_code")}
              </label>
              <input
                className="disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
                disabled={isDisabled || isMasterFieldDisabled}
                id="isrc_code"
                maxLength={128}
                onChange={handleChange}
                placeholder="ISRC FR-AB5-07-12345"
                type="text"
                value={String(item.isrc_code ?? "")}
              />
            </div>
          )}
          <div
            className="editor-form-input"
            data-tooltip-content={masterFieldTooltip}
            data-tooltip-id={APP_TOOLTIP_ID}
          >
            <label
              className="text-foreground py-1.25 text-[15px] font-medium"
              htmlFor="custom_id"
            >
              {t("custom_id")}
            </label>
            <input
              className="disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
              disabled={isDisabled || isMasterFieldDisabled}
              id="custom_id"
              maxLength={128}
              onChange={handleChange}
              type="text"
              value={String(item.custom_id ?? "")}
            />
          </div>
        </div>
      </div>

      {!isFAST && (
        <div className="editor-form-grid">
          <div className="editor-form-section">
            <div className="editor-form-section-title">
              {t("productionYear")}
            </div>
            <div
              className="editor-form-input"
              data-tooltip-content={masterFieldTooltip}
              data-tooltip-id={APP_TOOLTIP_ID}
              style={{ maxWidth: "150px" }}
            >
              <Select
                closeMenuOnSelect
                className="editor-select"
                classNamePrefix="slct"
                isDisabled={isDisabled || isMasterFieldDisabled}
                name="form-field-name"
                onChange={onProductionYear}
                options={getYears()}
                placeholder={t("select")}
                defaultValue={
                  item.production_date
                    ? {
                        label: moment(item.production_date).format("YYYY"),
                        value: item.production_date,
                      }
                    : null
                }
              />
            </div>
          </div>

          <div className="editor-form-section">
            <div className="editor-form-input-box-h">
              <div
                className="editor-form-input"
                data-tooltip-content={masterFieldTooltip}
                data-tooltip-id={APP_TOOLTIP_ID}
                style={{ maxWidth: "175px" }}
              >
                <label
                  className="text-foreground! mb-1.25! text-[15px] font-medium!"
                  htmlFor="publication_date"
                >
                  {t("publicationDate")}
                </label>
                <DateTimePicker
                  withoutTime
                  disabled={isDisabled || isMasterFieldDisabled}
                  placeholder={t("select")}
                  onDateTimeChange={(date) =>
                    handleChange({
                      target: { id: "publication_date", value: date },
                    })
                  }
                  value={
                    item.publication_date
                      ? new Date(item.publication_date as string)
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {!isMaster && !isFAST && (
        <div className="editor-form-section section-separator">
          <div className="editor-form-section-title">
            {t("linkFreeContent")}
          </div>
          <TrailerSelect
            isDisabled={isDisabled}
            onChange={onTrailer}
            trailerId={item.trailer_media_uuid}
          />
        </div>
      )}

      <ContentClassification
        isMasterFieldDisabled={isMasterFieldDisabled}
        masterFieldTooltip={masterFieldTooltip}
      />
      <div className="editor-form-section section-separator">
        <div className="editor-form-section-title">
          {t("editor-utils:variables")}
        </div>
        <div
          className="editor-form-input"
          data-tooltip-content={masterFieldTooltip}
          data-tooltip-id={APP_TOOLTIP_ID}
        >
          <VariableList
            isDisabled={isDisabled || isMasterFieldDisabled}
            onChange={handleVariableListChange}
            variables={item?.custom_params}
          />
        </div>
      </div>
      <InternalMediaComment
        isMasterFieldDisabled={isMasterFieldDisabled}
        masterFieldTooltip={masterFieldTooltip}
      />
    </div>
  );
}

function TabContentMetadata(): JSX.Element {
  const { item } = useContext<ContentContextValues>(ContentContext);

  if (item.type === "master") {
    return (
      <TabsMaster>
        <TabContentMetadataWithoutMasterContext />
      </TabsMaster>
    );
  }

  return <TabContentMetadataWithoutMasterContext />;
}

export default TabContentMetadata;
